import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function DopplerEffectCalculator() {
  const [frequency, setFrequency] = useState("");
  const [waveSpeed, setWaveSpeed] = useState("343");
  const [sourceVelocity, setSourceVelocity] = useState("");
  const [observerVelocity, setObserverVelocity] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculateDoppler = () => {
    const f0 = parseFloat(frequency);
    const v = parseFloat(waveSpeed);
    const vs = parseFloat(sourceVelocity);
    const vo = parseFloat(observerVelocity);

    if (isNaN(f0) || isNaN(v) || isNaN(vs) || isNaN(vo)) {
      toast.error("Please enter valid values");
      return;
    }

    // f = f0 * (v + vo) / (v - vs)
    const observedFreq = f0 * ((v + vo) / (v - vs));
    setResult(observedFreq);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setFrequency("");
    setWaveSpeed("343");
    setSourceVelocity("");
    setObserverVelocity("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Doppler Effect Calculator" 
      calculatorPath="/calculator/doppler-effect"
      category="physics"
      subcategory="Waves"
    >
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Doppler Effect Calculator</h1>
          <p className="text-muted-foreground">
            Calculate frequency shift due to relative motion
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: f = f₀ × (v + v₀) / (v - vₛ)</CardTitle>
            <CardDescription>
              Where f is observed frequency, f₀ is source frequency, v is wave speed, v₀ is observer velocity, vₛ is source velocity
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="frequency">Source Frequency (Hz)</Label>
                <Input
                  id="frequency"
                  type="number"
                  step="0.01"
                  placeholder="Enter source frequency"
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="waveSpeed">Wave Speed (m/s)</Label>
                <Input
                  id="waveSpeed"
                  type="number"
                  step="0.01"
                  placeholder="Speed of sound: 343 m/s"
                  value={waveSpeed}
                  onChange={(e) => setWaveSpeed(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sourceVelocity">Source Velocity (m/s)</Label>
                <Input
                  id="sourceVelocity"
                  type="number"
                  step="0.01"
                  placeholder="Positive if moving toward observer"
                  value={sourceVelocity}
                  onChange={(e) => setSourceVelocity(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">Positive: toward observer, Negative: away from observer</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="observerVelocity">Observer Velocity (m/s)</Label>
                <Input
                  id="observerVelocity"
                  type="number"
                  step="0.01"
                  placeholder="Positive if moving toward source"
                  value={observerVelocity}
                  onChange={(e) => setObserverVelocity(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">Positive: toward source, Negative: away from source</p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculateDoppler} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">
                Reset
              </Button>
            </div>

            {result !== null && (
              <>
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader>
                    <CardTitle className="text-xl">Result</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-primary">
                      {result.toFixed(2)} Hz
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Observed Frequency
                    </p>
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["doppler-effect"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">How to use</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>1. Enter the source frequency in Hertz</p>
                <p>2. Enter wave speed (default: 343 m/s for sound in air)</p>
                <p>3. Enter source velocity (+ toward, - away from observer)</p>
                <p>4. Enter observer velocity (+ toward, - away from source)</p>
                <p className="pt-2 font-medium text-foreground">
                  Example: Ambulance siren at 1000 Hz moving at 30 m/s toward stationary observer = 1096 Hz
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
